import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { format, differenceInSeconds } from 'date-fns';
import Loader from './components/section/Loader';

// Countdown Timer Component
const CountdownTimer = ({ targetDate }: { targetDate: Date }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const diffSeconds = differenceInSeconds(targetDate, now);
      
      if (diffSeconds <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(diffSeconds / (3600 * 24));
      const hours = Math.floor((diffSeconds % (3600 * 24)) / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = Math.floor(diffSeconds % 60);

      return { days, hours, minutes, seconds };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="grid grid-cols-4 gap-2 mt-4">
      <div className="flex flex-col items-center bg-blue-100 rounded-lg py-2">
        <span className="text-xl font-bold">{timeLeft.days}</span>
        <span className="text-xs text-gray-600">DAYS</span>
      </div>
      <div className="flex flex-col items-center bg-blue-100 rounded-lg py-2">
        <span className="text-xl font-bold">{timeLeft.hours}</span>
        <span className="text-xs text-gray-600">HRS</span>
      </div>
      <div className="flex flex-col items-center bg-blue-100 rounded-lg py-2">
        <span className="text-xl font-bold">{timeLeft.minutes}</span>
        <span className="text-xs text-gray-600">MIN</span>
      </div>
      <div className="flex flex-col items-center bg-blue-100 rounded-lg py-2">
        <span className="text-xl font-bold">{timeLeft.seconds}</span>
        <span className="text-xs text-gray-600">SEC</span>
      </div>
    </div>
  );
};

// Meal Schedule Component
const MealSchedule = ({ meals }: { meals: any[] }) => {
  if (!meals || meals.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No meal information available
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        {meals.map((meal, index) => (
          meal.selected && (
            <div 
              key={index} 
              className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-medium flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {meal.name}
            </div>
          )
        ))}
      </div>
    </div>
  );
};

const EventEntryPage = () => {
  const { eventID, viewerID } = useParams();
  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState<any>(null);
  const [rsvpData, setRsvpData] = useState<any>(null);
  const [error, setError] = useState('');

  // Fetch event and RSVP data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!eventID || !viewerID) {
          setError('Invalid event or viewer ID');
          setLoading(false);
          return;
        }

        // Fetch RSVP response
        const rsvpQuery = query(
          collection(db, 'rsvpResponses'),
          where('eventID', '==', eventID),
          where('viewerId', '==', viewerID)
        );
        const querySnapshot = await getDocs(rsvpQuery);
        
        if (querySnapshot.empty) {
          setError('Invalid entry code');
          setLoading(false);
          return;
        }
        
        const rsvpDoc = querySnapshot.docs[0];
        const rsvpData = rsvpDoc.data();
        setRsvpData(rsvpData);
        
        // Check if user is attending
        if (!rsvpData.attending) {
          setError('You are not attending this event');
          setLoading(false);
          return;
        }
        
        // Fetch event data
        const eventDocRef = doc(db, "events", eventID);
        const eventDoc = await getDoc(eventDocRef);
        
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          setEventData({
            ...eventData,
            startDate: eventData.startDate.toDate(),
            endDate: eventData.endDate.toDate(),
            days: eventData.days.map((day: any) => ({
              ...day,
              date: day.date.toDate(),
              startTime: day.startTime.toDate(),
              endTime: day.endTime.toDate()
            }))
          });
        } else {
          setError('Event not found');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load entry details');
        setLoading(false);
      }
    };

    fetchData();
  }, [eventID, viewerID]);

  if (loading) return <Loader />;
  
  // Handle error states
  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
        <div className="text-red-500 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        
      </div>
    </div>
  );
  
  // Find the first day with meals
  const mealDay = eventData.days.find((day: any) => day.meals && day.meals.length > 0);
  const hasMeals = mealDay && mealDay.meals.some((meal: any) => meal.selected);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full border border-blue-100">
        {/* Pass Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-indigo-800">EVENT PASS</h1>
          <div className="w-24 h-1 bg-indigo-500 mx-auto mt-2 rounded-full"></div>
        </div>
        
        {/* Event Card */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-2xl mb-8 shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{eventData.title}</h2>
              <div className="flex items-center mt-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">
                  {format(eventData.startDate, 'EEE, MMM dd, yyyy')}
                </p>
              </div>
              <div className="flex items-center mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">
                  {format(eventData.startDate, 'hh:mm a')} - {format(eventData.endDate, 'hh:mm a')}
                </p>
              </div>
            </div>
            <div className="bg-white/20 p-2 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {/* Location */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Location</span>
            </div>
            <p className="text-sm">{eventData.eventAddress || 'Venue TBD'}</p>
            {eventData.eventAddress && (
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventData.eventAddress)}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-indigo-600 hover:underline mt-1 block"
              >
                View on Map
              </a>
            )}
          </div>

          {/* Meal Schedule */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Meal Schedule</span>
            </div>
            
            {hasMeals ? (
              <MealSchedule meals={mealDay.meals} />
            ) : (
              <div className="text-sm text-gray-500 italic mt-2">
                No meals included
              </div>
            )}
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="bg-indigo-50 p-5 rounded-xl mb-8 border border-indigo-100">
          <div className="flex items-center justify-center mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-center text-indigo-800">Event Starts In</h3>
          </div>
          <CountdownTimer targetDate={eventData.startDate} />
        </div>

        {/* Guest Details */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100">
          <h3 className="text-lg font-semibold text-center mb-4 text-indigo-800">GUEST INFORMATION</h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-blue-100">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-gray-600">Guest Name</span>
              </div>
              <p className="text-lg font-medium">{rsvpData.userName}</p>
            </div>
            
            <div className="flex justify-between items-center pb-3 border-b border-blue-100">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-gray-600">Attendees</span>
              </div>
              <p className="text-lg font-medium">{rsvpData.numberOfAttendees}</p>
            </div>
            
            {rsvpData.eventAttended && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-600">Checked In</span>
                </div>
                <p className="text-sm text-green-600 font-medium">Successfully checked in</p>
              </div>
            )}
          </div>
        </div>

        {/* Pass Footer */}
        <div className="text-center mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Present this pass at event entry. Valid for {rsvpData.numberOfAttendees} {rsvpData.numberOfAttendees > 1 ? 'attendees' : 'attendee'}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventEntryPage;